import { useT } from '@planetra/i18n';
import { CloudOff } from 'lucide-react';
import { useConnectivity } from '../api/context';

/**
 * PRD §15 — "network loss mid-session: offline banner, no silent data loss".
 *
 * One of only two global surfaces P0-WEB-04 adds. §15 requires failures to be
 * designed per scenario rather than dropped into a generic toast, so everything
 * else surfaces where it happened: a form error next to the field, a panel
 * error inside the panel. Losing the connection is genuinely global, because it
 * explains every other thing on the page at once.
 *
 * Rendered as a reserved row rather than an overlay, and always present in the
 * layout with zero height when online, so its arrival does not push the page
 * down — planning.md §6, no layout shift on data arrival.
 */
export function OfflineBanner() {
  const { t } = useT();
  const reachable = useConnectivity();

  return (
    <div
      // Polite, not assertive: the user has probably noticed, and interrupting
      // whatever a screen reader is mid-sentence on does not help them.
      role="status"
      aria-live="polite"
      aria-label={t('errors:api.offlineBanner.label')}
      className={
        reachable
          ? 'h-0 overflow-hidden'
          : 'flex items-center gap-2 border-b border-line bg-surface-raised px-4 py-2 text-sm text-fg-muted'
      }
    >
      {!reachable && (
        <>
          <CloudOff aria-hidden className="size-4 shrink-0" />
          <span>{t('errors:api.offlineBanner.message')}</span>
        </>
      )}
    </div>
  );
}
